/**
 * 音频版播放策略 - 纯音频播放
 * 包装 AudioController，提供纯音频播放功能
 */
import { PlayStrategy } from './PlayStrategy.js';
import AudioController from '../AudioController.js';
import {
  isExternalPluginSource,
  isPlatformAccountSource,
  getExternalPluginMediaSource
} from '../../music/plugins/externalPluginResolver.js';

const isPluginResolvableSong = (song) =>
  isExternalPluginSource(song?.source) || isPlatformAccountSource(song?.source) || song?.songmid || song?.mid;

export class AudioStrategy extends PlayStrategy {
  constructor() {
    super();
    
    // 🔥 每次创建新策略时，创建新的 AudioController 实例
    // 确保状态完全隔离
    this.audioController = new AudioController();
    
    // 绑定基础播放器的事件到策略事件系统
    this.bindAudioControllerEvents();
    
    console.log('🎵 [AudioStrategy] 音频版策略创建完成');
  }
  
  /**
   * 绑定 AudioController 事件到策略事件系统
   */
  bindAudioControllerEvents() {
    // 播放事件（调用 play() 后触发）
    this.audioController.on('play', () => {
      this.emit('play', {});
    });
    
    // 🔥 新增：正在播放事件（音频真正开始播放时触发，用户能听到声音）
    // 这是最准确的播放状态标志
    this.audioController.on('playing', () => {
      console.log('🎵 [AudioStrategy] 🔥 收到 AudioController 的 playing 事件，转发给策略管理器');
      this.emit('playing', {});
      console.log('🎵 [AudioStrategy] playing 事件已转发给策略管理器');
    });
    
    // 暂停事件
    this.audioController.on('pause', () => {
      this.emit('pause', {});
    });
    
    // 时间更新事件
    this.audioController.on('timeupdate', (data) => {
      this.emit('timeupdate', {
        currentTime: data.currentTime,
        duration: data.duration
      });
    });
    
    // 播放结束事件
    this.audioController.on('ended', () => {
      this.emit('ended', {});
    });
    
    // 错误事件
    this.audioController.on('error', (data) => {
      this.emit('error', { error: data.error });
    });
    
    // 元数据加载完成事件
    this.audioController.on('loadedmetadata', (data) => {
      this.emit('loadedmetadata', { duration: data.duration });
    });
    
    // 可以播放事件
    this.audioController.on('canplay', () => {
      this.emit('canplay', {});
    });
  }
  
  /**
   * 加载歌曲
   * @param {Object} song - 歌曲对象
   * @returns {Promise<void>}
   */
  async load(song) {
    if (!song) {
      throw new Error('歌曲对象不能为空');
    }
    
    console.log('🎵 [AudioStrategy] 加载音频歌曲:', song.title);
    console.log('🔍 [AudioStrategy] 检查歌曲属性:', {
      isOnline: song.isOnline,
      isExternalWithResources: song.isExternalWithResources,
      playUrl: song.playUrl,
      streamUrl: song.streamUrl,
      url: song.url,
      audioUrl: song.audioUrl
    });
    
    // 获取音频URL
    let audioUrl = song.audioUrl || song.url;
    
    if (!audioUrl) {
      // 🔥 关键修复：优先检查 isExternalWithResources，即使 playUrl 为 undefined，也尝试从其他属性获取
      if (song.isExternalWithResources) {
        // 尝试从多个属性获取播放URL
        audioUrl = song.playUrl || song.streamUrl || song.url;
        if (audioUrl) {
          console.log('🌐 [AudioStrategy] 使用外部音乐的播放URL:', audioUrl);
          console.log('🌐 [AudioStrategy] 来源:', {
            playUrl: song.playUrl ? 'playUrl' : null,
            streamUrl: song.streamUrl ? 'streamUrl' : null,
            url: song.url ? 'url' : null
          });
        } else {
          console.warn('⚠️ [AudioStrategy] 检测到外部音乐标记，但未找到播放URL');
          
          // 🔥 新增：如果是QQ音乐插件的外部音乐，尝试通过插件获取播放URL
          if (isPluginResolvableSong(song)) {
            console.log('🎵 [AudioStrategy] 检测到插件音源歌曲，尝试获取播放URL...', song.source);
            try {
              const mediaSource = await getExternalPluginMediaSource(song, 'high');
              if (mediaSource && mediaSource.url) {
                audioUrl = mediaSource.url;
                // 将获取到的URL设置到歌曲对象上，以便后续使用
                song.playUrl = mediaSource.url;
                song.streamUrl = mediaSource.url;
                song.url = mediaSource.url;
                console.log('✅ [AudioStrategy] 成功从插件音源获取播放URL:', audioUrl);
                
                // 🚀 优化：如果 getMediaSource 同时返回了歌词，保存到歌曲对象上
                if (mediaSource.lyric) {
                  console.log('🎵 [AudioStrategy] getMediaSource 同时返回了歌词，保存到歌曲对象');
                  song._cachedLyric = mediaSource.lyric;
                  // 如果歌曲有 lyricsUrl 属性，也可以设置
                  if (!song.lyricsUrl) {
                    song.lyricsUrl = mediaSource.lyric.lrc || '';
                  }
                }
              }
            } catch (error) {
              console.error('❌ [AudioStrategy] 从插件音源获取播放URL失败:', error);
              // 继续执行，让后续的错误处理逻辑处理
            }
          }
        }
      } else if (song.isOnline) {
        // 尝试从多个属性获取播放URL
        audioUrl = song.playUrl || song.streamUrl || song.url;
        if (audioUrl) {
          console.log('🌐 [AudioStrategy] 使用在线音乐的播放URL:', audioUrl);
        } else {
          // 🔥 新增：如果是QQ音乐插件的在线音乐，尝试通过插件获取播放URL
          if (isPluginResolvableSong(song)) {
            console.log('🎵 [AudioStrategy] 检测到插件音源在线歌曲，尝试获取播放URL...', song.source);
            try {
              const mediaSource = await getExternalPluginMediaSource(song, 'high');
              if (mediaSource && mediaSource.url) {
                audioUrl = mediaSource.url;
                song.playUrl = mediaSource.url;
                song.streamUrl = mediaSource.url;
                song.url = mediaSource.url;
                console.log('✅ [AudioStrategy] 成功从插件音源获取播放URL:', audioUrl);
                
                // 🚀 优化：如果 getMediaSource 同时返回了歌词，保存到歌曲对象上
                if (mediaSource.lyric) {
                  console.log('🎵 [AudioStrategy] getMediaSource 同时返回了歌词，保存到歌曲对象');
                  song._cachedLyric = mediaSource.lyric;
                  // 设置 lyricsUrl，确保策略工厂能识别为歌词版
                  if (!song.lyricsUrl) {
                    song.lyricsUrl = mediaSource.lyric.lrc || '';
                  }
                }
              }
            } catch (error) {
              console.error('❌ [AudioStrategy] 从插件音源获取播放URL失败:', error);
            }
          }
        }
      }
      
      // 如果还是没有找到URL，根据其他信息构造URL
      if (!audioUrl) {
        if (song.streamUrl) {
          audioUrl = song.streamUrl;
          console.log('🌐 [AudioStrategy] 使用streamUrl:', audioUrl);
        } else if (song.fileSource === 'onedrive') {
          audioUrl = `${import.meta.env.VITE_APP_AUDIO_STREAM_BASE_URL}/api/music/${song.id}/onedrive-stream`;
          console.log('💾 [AudioStrategy] 使用OneDrive流式接口:', audioUrl);
        } else {
          // 🔥 关键修复：如果是外部音乐但没有URL，不应该使用本地流式接口
          if (song.isExternalWithResources || song.isOnline) {
            console.error('❌ [AudioStrategy] 外部音乐或在线音乐缺少播放URL，无法播放');
            throw new Error('外部音乐缺少播放URL');
          }
          audioUrl = `${import.meta.env.VITE_APP_AUDIO_STREAM_BASE_URL}/api/music/${song.id}/stream`;
          console.log('💾 [AudioStrategy] 使用本地流式接口:', audioUrl);
        }
      }
    }
    
    console.log('🎵 [AudioStrategy] 最终音频URL:', audioUrl);
    
    // 🔥 关键修复：传递歌曲对象而不是URL，以便 AudioController 可以获取歌曲信息用于缓存监听
    // 加载音频（传递歌曲对象，AudioController 会自动提取 URL）
    await this.audioController.loadSong(song);
    
    console.log('🎵 [AudioStrategy] 音频加载完成');
  }
  
  /**
   * 播放
   * @returns {Promise<void>}
   */
  async play() {
    console.log('🎵 [AudioStrategy] 开始播放');
    await this.audioController.play();
  }
  
  /**
   * 暂停
   */
  pause() {
    console.log('🎵 [AudioStrategy] 暂停播放');
    this.audioController.pause();
  }
  
  /**
   * 停止
   */
  stop() {
    console.log('🎵 [AudioStrategy] 停止播放');
    this.audioController.stop();
  }
  
  /**
   * 设置播放时间
   * @param {number} time - 播放时间（秒）
   */
  setCurrentTime(time, options) {
    this.audioController.setCurrentTime(time, options);
  }
  
  /**
   * 获取当前播放时间
   * @returns {number}
   */
  getCurrentTime() {
    if (!this.audioController) {
      return 0;
    }
    return this.audioController.getCurrentTime();
  }
  
  /**
   * 获取总时长
   * @returns {number}
   */
  getDuration() {
    return this.audioController.getDuration();
  }
  
  /**
   * 是否正在播放
   * @returns {boolean}
   */
  isPlaying() {
    return this.audioController.isPlaying();
  }
  
  /**
   * 🔥 新增：全屏相关方法
   */
  
  /**
   * 检查是否处于全屏状态
   * @returns {boolean}
   */
  isInFullscreen() {
    if (this.fullscreenControls && this.fullscreenControls.isInFullscreen) {
      return this.fullscreenControls.isInFullscreen('audio');
    }
    return false;
  }
  
  /**
   * 进入全屏（如果需要的话）
   * 当切换歌曲时，如果之前处于全屏状态，会调用此方法
   * @returns {Promise<void>}
   */
  async enterFullscreenIfNeeded() {
    if (this.fullscreenControls && this.fullscreenControls.enterFullscreen) {
      console.log('🎵 [AudioStrategy] 进入音频全屏模式');
      await this.fullscreenControls.enterFullscreen('audio');
    }
  }
  
  /**
   * 退出全屏
   * @returns {Promise<void>}
   */
  async exitFullscreen() {
    if (this.fullscreenControls && this.fullscreenControls.exitFullscreen) {
      console.log('🎵 [AudioStrategy] 退出音频全屏模式');
      await this.fullscreenControls.exitFullscreen('audio');
    }
  }
  
  /**
   * 清理资源
   */
  cleanup() {
    console.log('🧹 [AudioStrategy] 开始清理资源');
    
    // 清理 AudioController
    if (this.audioController) {
      // 停止高频率时间更新
      if (this.audioController.stopHighFreqTimeUpdate) {
        this.audioController.stopHighFreqTimeUpdate();
      }
      
      // 清理 AudioController
      this.audioController.cleanup();
      this.audioController = null;
    }
    
    // 调用父类清理方法（清理事件监听器）
    super.cleanup();
    
    console.log('✅ [AudioStrategy] 资源清理完成');
  }
}

